import {
  CARD_PAD_Y,
  CIRCLE_SIZE,
  SOLO_UNITED_GAP,
} from './chatteaser3.constants';

type AvatarCollapseShape = {
  height: number;
  translateY: number;
  nameOpacity: number;
};

type MessageRectShapeLocal = {
  top: number;
  height: number;
};

type ExpandLayoutShapeLocal = {
  soloBottom: number;
  maxScroll: number;
  wrapperHeight: number;
  rects: MessageRectShapeLocal[];
};

type ScrollerSpacingShape = {
  marginTop: number;
  paddingTop: number;
};

interface MeasureChatTeaser3LayoutInput {
  wrapper: HTMLDivElement | null;
  soloContent: HTMLDivElement | null;
  unitedHeader: HTMLDivElement | null;
  unitedScroller: HTMLDivElement | null;
  messagesInner: HTMLDivElement | null;
}

function getClientHeight(element: HTMLElement | null): number {
  return element?.clientHeight ?? 0;
}

function getOffsetHeight(element: HTMLElement | null): number {
  return element?.offsetHeight ?? 0;
}

function getScrollHeight(element: HTMLElement | null): number {
  return element?.scrollHeight ?? 0;
}

function readScrollerSpacing(scroller: HTMLElement | null): ScrollerSpacingShape {
  if (!scroller) return { marginTop: 0, paddingTop: 0 };

  const computedStyle = window.getComputedStyle(scroller);
  const marginTop = Number.parseFloat(computedStyle.marginTop);
  const paddingTop = Number.parseFloat(computedStyle.paddingTop);

  return {
    marginTop: Number.isFinite(marginTop) ? marginTop : 0,
    paddingTop: Number.isFinite(paddingTop) ? paddingTop : 0,
  };
}

function getMessageRects(input: {
  inner: HTMLDivElement | null;
  topOffset: number;
}): MessageRectShapeLocal[] {
  const { inner, topOffset } = input;
  if (!inner) return [];

  const innerTop = inner.getBoundingClientRect().top;
  return Array.from(inner.children).map((child) => {
    const rect = child.getBoundingClientRect();
    return { top: topOffset + rect.top - innerTop, height: rect.height };
  });
}

export function measureChatTeaser3Layout({
  wrapper,
  soloContent,
  unitedHeader,
  unitedScroller,
  messagesInner,
}: MeasureChatTeaser3LayoutInput): ExpandLayoutShapeLocal {
  const wrapperHeight = getClientHeight(wrapper);
  const soloContentHeight = getOffsetHeight(soloContent);
  const headerHeight = getOffsetHeight(unitedHeader);
  const scrollerSpacing = readScrollerSpacing(unitedScroller);
  const contentTop = scrollerSpacing.paddingTop;
  const contentHeight = contentTop + getScrollHeight(messagesInner);
  const rects = getMessageRects({ inner: messagesInner, topOffset: contentTop });
  const soloBottom = 2 * CARD_PAD_Y + soloContentHeight + SOLO_UNITED_GAP;
  const scrollerHeightWhenFull = Math.max(
    0,
    wrapperHeight - headerHeight - scrollerSpacing.marginTop,
  );
  const overflowWhenFull = Math.max(0, contentHeight - scrollerHeightWhenFull);

  return { soloBottom, maxScroll: soloBottom + overflowWhenFull, wrapperHeight, rects };
}

export function computeCollapse(
  rect: MessageRectShapeLocal | undefined,
  messageScroll: number,
): AvatarCollapseShape | undefined {
  if (!rect) return undefined;

  const scrolledPast = messageScroll - rect.top;
  if (scrolledPast <= 0) return undefined;

  const height = Math.max(CIRCLE_SIZE, rect.height - scrolledPast);
  const translateY = Math.min(scrolledPast, rect.height - height);
  const denominator = rect.height - CIRCLE_SIZE;
  const nameOpacity = denominator > 0
    ? Math.max(0, Math.min(1, (height - CIRCLE_SIZE) / denominator))
    : 0;

  return { height, translateY, nameOpacity };
}
